import React, { useState } from 'react';
import { Type, ZoomIn, ZoomOut, RotateCcw, MoveVertical, BetweenVerticalEnd, Palette, Check, Eye, EyeOff, Sliders, ChevronDown, ChevronUp } from 'lucide-react';

export type FontFamilyOption = 'sans' | 'serif' | 'mono';
export type FontSizeOption = 'sm' | 'base' | 'lg' | 'xl';
export type LineSpacingOption = 'tight' | 'normal' | 'relaxed' | 'loose';
export type ParaSpacingOption = 'compact' | 'normal' | 'relaxed' | 'spacious';
export type ReaderThemeOption = 'white' | 'sepia' | 'cream' | 'dark' | 'mint';

interface ReadingControlsBarProps {
  fontFamily: FontFamilyOption;
  fontSize: FontSizeOption;
  lineSpacing: LineSpacingOption;
  paraSpacing: ParaSpacingOption;
  readerTheme: ReaderThemeOption;
  onChangeFontFamily: (font: FontFamilyOption) => void;
  onChangeFontSize: (size: FontSizeOption) => void;
  onChangeLineSpacing: (line: LineSpacingOption) => void;
  onChangeParaSpacing: (para: ParaSpacingOption) => void;
  onChangeReaderTheme: (theme: ReaderThemeOption) => void;
  onResetControls: () => void;
  isZenMode: boolean;
  onChangeZenMode: (zen: boolean) => void;
}

const themePresets: { id: ReaderThemeOption; title: string; dotClass: string }[] = [
  { id: 'white', title: 'Putih / Terang (Default)', dotClass: 'bg-white border-slate-300' },
  { id: 'sepia', title: 'Sepia / Klasik Hangat', dotClass: 'bg-[#f8f3e6] border-[#d8c8b0]' },
  { id: 'cream', title: 'Kertas Gading / Cream Soft', dotClass: 'bg-[#f7f5f0] border-[#dcd7cc]' },
  { id: 'dark', title: 'Malam / Mode Gelap', dotClass: 'bg-[#0f172a] border-[#475569]' },
  { id: 'mint', title: 'Mint / Meredakan Lelah Mata', dotClass: 'bg-[#edf6ed] border-[#a8d5ac]' },
];

export const ReadingControlsBar: React.FC<ReadingControlsBarProps> = ({
  fontFamily,
  fontSize,
  lineSpacing,
  paraSpacing,
  readerTheme,
  onChangeFontFamily,
  onChangeFontSize,
  onChangeLineSpacing,
  onChangeParaSpacing,
  onChangeReaderTheme,
  onResetControls,
  isZenMode,
  onChangeZenMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isCustomized =
    fontFamily !== 'sans' ||
    fontSize !== 'base' ||
    lineSpacing !== 'normal' ||
    paraSpacing !== 'normal' ||
    readerTheme !== 'white';

  return (
    <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-3 mb-6 shadow-xs">
      {/* Header Row / Compact Toggle Bar */}
      <div 
        className="flex items-center justify-between gap-3 cursor-pointer select-none" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2.5 text-slate-700">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Sliders className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-slate-900 leading-none">
              Tata Letak & Tampilan Membaca
            </h3>
            <p className="text-[10px] text-slate-500 mt-1">
              {isExpanded ? 'Klik untuk sembunyikan pengaturan' : 'Sesuaikan jenis huruf, spasi, warna latar lembar baca'}
            </p>
          </div>
        </div>

        {/* Compact Right Actions */}
        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          {/* Zen Mode Button (Always accessible instantly) */}
          <button
            type="button"
            onClick={() => onChangeZenMode(!isZenMode)}
            className={`px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1 text-[10px] font-bold cursor-pointer shadow-2xs ${
              isZenMode
                ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200'
            }`}
            title={isZenMode ? 'Keluar dari Mode Zen' : 'Masuk ke Mode Zen (Tanpa Gangguan)'}
          >
            {isZenMode ? (
              <>
                <EyeOff className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Zen Aktif</span>
              </>
            ) : (
              <>
                <Eye className="w-3.5 h-3.5 text-amber-500" />
                <span className="hidden sm:inline">Mode Zen</span>
              </>
            )}
          </button>

          {/* Expand Chevron Toggle */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-lg bg-white hover:bg-slate-200 text-slate-500 border border-slate-200 transition-all cursor-pointer shadow-2xs"
            title={isExpanded ? 'Sembunyikan Pengaturan' : 'Tampilkan Pengaturan'}
          >
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Expanded Controls Grid Dashboard with Smooth Transition */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded
            ? 'grid-rows-[1fr] opacity-100 mt-3 pt-3 border-t border-slate-200/60'
            : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
      >
        <div className="overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Card 1: Font Style */}
            <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs flex flex-col justify-between">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">
                Gaya Fonta (Jenis Huruf)
              </span>
              <div className="grid grid-cols-3 gap-1 bg-slate-50 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => onChangeFontFamily('sans')}
                  className={`py-1 rounded-md text-[11px] font-sans font-bold transition-all cursor-pointer ${
                    fontFamily === 'sans'
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                >
                  Sans
                </button>
                <button
                  type="button"
                  onClick={() => onChangeFontFamily('serif')}
                  className={`py-1 rounded-md text-[11px] font-serif font-bold transition-all cursor-pointer ${
                    fontFamily === 'serif'
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                >
                  Serif
                </button>
                <button
                  type="button"
                  onClick={() => onChangeFontFamily('mono')}
                  className={`py-1 rounded-md text-[11px] font-mono font-bold transition-all cursor-pointer ${
                    fontFamily === 'mono'
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                >
                  Mono
                </button>
              </div>
            </div>

            {/* Card 2: Font Size */}
            <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs flex flex-col justify-between">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">
                Ukuran Teks
              </span>
              <div className="flex items-center justify-between bg-slate-50 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    if (fontSize === 'xl') onChangeFontSize('lg');
                    else if (fontSize === 'lg') onChangeFontSize('base');
                    else if (fontSize === 'base') onChangeFontSize('sm');
                  }}
                  disabled={fontSize === 'sm'}
                  className="p-1 rounded-md text-slate-600 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all cursor-pointer"
                  title="Kecilkan ukuran teks"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>

                <span className="text-[11px] font-bold text-slate-700 min-w-[3rem] text-center">
                  {fontSize === 'sm' && 'Kecil'}
                  {fontSize === 'base' && 'Sedang'}
                  {fontSize === 'lg' && 'Besar'}
                  {fontSize === 'xl' && 'Jumbo'}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    if (fontSize === 'sm') onChangeFontSize('base');
                    else if (fontSize === 'base') onChangeFontSize('lg');
                    else if (fontSize === 'lg') onChangeFontSize('xl');
                  }}
                  disabled={fontSize === 'xl'}
                  className="p-1 rounded-md text-slate-600 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all cursor-pointer"
                  title="Besarkan ukuran teks"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Card 3: Spacing Controls */}
            <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs flex flex-col justify-between">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">
                Tata Letak Spasi
              </span>
              <div className="grid grid-cols-2 gap-1.5">
                {/* Line Spacing */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[8px] font-bold text-slate-400 uppercase">Baris</label>
                  <select
                    value={lineSpacing}
                    onChange={(e) => onChangeLineSpacing(e.target.value as LineSpacingOption)}
                    className="text-[10px] font-bold bg-slate-50 border border-slate-100 p-1 rounded-md text-slate-700 outline-none cursor-pointer w-full"
                  >
                    <option value="tight">Rapat</option>
                    <option value="normal">Normal</option>
                    <option value="relaxed">Renggang</option>
                    <option value="loose">Sangat Longgar</option>
                  </select>
                </div>

                {/* Paragraph Spacing */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[8px] font-bold text-slate-400 uppercase">Paragraf</label>
                  <select
                    value={paraSpacing}
                    onChange={(e) => onChangeParaSpacing(e.target.value as ParaSpacingOption)}
                    className="text-[10px] font-bold bg-slate-50 border border-slate-100 p-1 rounded-md text-slate-700 outline-none cursor-pointer w-full"
                  >
                    <option value="compact">Ringkas</option>
                    <option value="normal">Normal</option>
                    <option value="relaxed">Renggang</option>
                    <option value="spacious">Sangat Longgar</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Card 4: Reader background colors */}
            <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">
                  Lembar Bacaan
                </span>
                {isCustomized && (
                  <button
                    type="button"
                    onClick={onResetControls}
                    className="text-[9px] font-bold text-rose-500 hover:text-rose-700 cursor-pointer flex items-center gap-0.5"
                    title="Riset Pengaturan Tampilan Kebawaan"
                  >
                    <RotateCcw className="w-2.5 h-2.5" />
                    <span>Riset</span>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between gap-1 bg-slate-50 p-1 rounded-lg">
                {themePresets.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onChangeReaderTheme(t.id)}
                    className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all cursor-pointer ${t.dotClass} ${
                      readerTheme === t.id
                        ? 'ring-2 ring-indigo-600 ring-offset-1 scale-105 shadow-2xs'
                        : 'hover:scale-105 opacity-80 hover:opacity-100'
                    }`}
                    title={t.title}
                  >
                    {readerTheme === t.id && (
                      <Check className={`w-3 h-3 ${t.id === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
